import React, { useState, useEffect } from 'react';
import { useProjects } from '../contexts/ProjectsContext';

const WORKSTREAM_FIELDS = {
  land: [
    { name: 'ownership_type', label: 'Ownership type', type: 'select',
      options: ['', 'private', 'public', 'state_forest', 'mixed', 'unknown'] },
    { name: 'landowner_name', label: 'Landowner / contact person', type: 'text' },
    { name: 'landowner_contact', label: 'Email / phone', type: 'text' },
    { name: 'parcels_count', label: 'Number of parcels', type: 'number' },
    { name: 'option_status', label: 'Option status', type: 'select',
      options: ['', 'not_started', 'contacted', 'negotiating', 'signed', 'expired'] },
    { name: 'option_signed_date', label: 'Option signed', type: 'date' },
    { name: 'option_expiry_date', label: 'Option expiry', type: 'date',
      alert: (val) => {
        if (!val) return null;
        const days = Math.ceil((new Date(val) - new Date()) / 86400000);
        if (days < 0) return { level: 'red', text: 'EXPIRED' };
        if (days < 30) return { level: 'amber', text: `Expires in ${days} days` };
        return null;
      }},
    { name: 'acquisition_cost_estimate', label: 'Estimated cost (\u20ac)', type: 'currency' },
    { name: 'notes', label: 'Notes', type: 'textarea' },
  ],
  grid: [
    { name: 'grid_company', label: 'Grid operator', type: 'text', placeholder: 'e.g. Fingrid' },
    { name: 'application_submitted', label: 'Application submitted', type: 'date' },
    { name: 'reference_number', label: 'Reference / ticket number', type: 'text' },
    { name: 'capacity_requested_mw', label: 'Capacity requested (MW)', type: 'number' },
    { name: 'status', label: 'Connection status', type: 'select',
      options: ['', 'pre_application', 'submitted', 'offer_received', 'accepted', 'connected'] },
    { name: 'connection_cost_estimate', label: 'Connection cost estimate (\u20ac)', type: 'currency' },
    { name: 'expected_connection_date', label: 'Expected connection date', type: 'date' },
    { name: 'notes', label: 'Notes', type: 'textarea' },
  ],
  environment: [
    { name: 'eia_required', label: 'EIA required?', type: 'select',
      options: ['', 'unknown', 'yes', 'no', 'screening_needed'] },
    { name: 'eia_status', label: 'EIA status', type: 'select',
      options: ['', 'not_started', 'scoping', 'field_survey', 'report_draft', 'submitted', 'approved'] },
    { name: 'eia_consultant', label: 'EIA consultant', type: 'text' },
    { name: 'species_survey_status', label: 'Species survey', type: 'select',
      options: ['', 'not_needed', 'not_started', 'planned', 'in_progress', 'completed'] },
    { name: 'species_survey_result', label: 'Survey findings', type: 'textarea' },
    { name: 'natura_assessment_needed', label: 'Natura 2000 assessment needed?', type: 'select',
      options: ['', 'unknown', 'yes', 'no'] },
    { name: 'notes', label: 'Notes', type: 'textarea' },
  ],
  planning: [
    { name: 'current_zoning', label: 'Current zoning designation', type: 'text' },
    { name: 'rezoning_needed', label: 'Rezoning needed?', type: 'select',
      options: ['', 'unknown', 'yes', 'no'] },
    { name: 'building_permit_status', label: 'Building permit status', type: 'select',
      options: ['', 'not_started', 'pre_consultation', 'submitted', 'under_review', 'approved', 'rejected'] },
    { name: 'municipality_contact', label: 'Municipality contact', type: 'text' },
    { name: 'estimated_permit_timeline_months', label: 'Estimated timeline (months)', type: 'number' },
    { name: 'notes', label: 'Notes', type: 'textarea' },
  ],
  financials: [
    { name: 'land_cost', label: 'Land cost (\u20ac)', type: 'currency' },
    { name: 'grid_connection_cost', label: 'Grid connection cost (\u20ac)', type: 'currency' },
    { name: 'site_preparation_cost', label: 'Site preparation cost (\u20ac)', type: 'currency' },
    { name: 'total_capex_estimate', label: 'Total CAPEX estimate (\u20ac)', type: 'currency' },
    { name: 'power_price_eur_mwh', label: 'Expected power price (\u20ac/MWh)', type: 'number' },
    { name: 'notes', label: 'Notes', type: 'textarea' },
  ],
};

function FormField({ field, value, onChange }) {
  const val = value ?? '';
  const alert = field.alert ? field.alert(val) : null;

  switch (field.type) {
    case 'select':
      return (
        <div className="ws-field">
          <label>{field.label}</label>
          <select value={val} onChange={e => onChange(e.target.value || null)}>
            {field.options.map(o => (
              <option key={o} value={o}>{o ? o.replace(/_/g, ' ') : '-- select --'}</option>
            ))}
          </select>
          {alert && <span className={`ws-alert ws-alert-${alert.level}`}>{alert.text}</span>}
        </div>
      );
    case 'textarea':
      return (
        <div className="ws-field">
          <label>{field.label}</label>
          <textarea value={val} onChange={e => onChange(e.target.value || null)}
                    placeholder={field.placeholder} rows={3} />
        </div>
      );
    case 'currency':
      return (
        <div className="ws-field">
          <label>{field.label}</label>
          <input type="number" value={val} onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
                 placeholder={field.placeholder} min={0} step={1000} />
        </div>
      );
    case 'number':
      return (
        <div className="ws-field">
          <label>{field.label}</label>
          <input type="number" value={val} onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
                 placeholder={field.placeholder} />
        </div>
      );
    case 'date':
      return (
        <div className="ws-field">
          <label>{field.label}</label>
          <input type="date" value={val} onChange={e => onChange(e.target.value || null)} />
          {alert && <span className={`ws-alert ws-alert-${alert.level}`}>{alert.text}</span>}
        </div>
      );
    default:
      return (
        <div className="ws-field">
          <label>{field.label}</label>
          <input type="text" value={val} onChange={e => onChange(e.target.value || null)}
                 placeholder={field.placeholder} />
        </div>
      );
  }
}

export default function WorkstreamForm({ projectId, workstream, data, onSave }) {
  const fields = WORKSTREAM_FIELDS[workstream];
  const [formData, setFormData] = useState(data || {});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const { updateProject } = useProjects();

  useEffect(() => { setFormData(data || {}); setDirty(false); }, [data]);

  if (!fields) return <div className="ws-empty">Unknown workstream: {workstream}</div>;

  const handleChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const updated = await updateProject(projectId, { [workstream]: formData });
    if (updated) {
      setDirty(false);
      onSave?.(formData);
    }
    setSaving(false);
  };

  return (
    <div className="workstream-form">
      {fields.map(field => (
        <FormField key={field.name} field={field}
                   value={formData[field.name]}
                   onChange={val => handleChange(field.name, val)} />
      ))}
      <div className="ws-actions">
        <button className="ws-save-btn" onClick={handleSave} disabled={!dirty || saving}>
          {saving ? 'Saving...' : dirty ? 'Save changes' : 'Saved'}
        </button>
      </div>
    </div>
  );
}

export { WORKSTREAM_FIELDS };
